import React from 'react'
import { APIEntity } from 'services/api/entities'
import { TailoredProduct } from 'services/api/entities/catalog/types'
import { Customer } from 'services/api/entities/crm/pmi'
import { Supplier } from 'services/api/entities/crm/suppliers'
import { useKGetList } from 'services/hooks/useKGetList'
import { getById } from 'services/utils'
import { WithIdAndName } from 'types/common'
import CustomReferenceInput, {
  isProductVisibleInStore,
} from 'ui-shared/CustomInputs/CustomReferenceInput'
import EventMainProductSelectionInput, {
  EventMainProductSelectionInputProps,
} from './EventMainProductSelectionInput'
import { useFormContext } from 'react-hook-form'
import { MappedEnrichedEvent } from 'services/api/entities/events/types/internal/event-models'
import { MainProduct } from 'services/api/entities/catalog/mainProduct/types/internal/mainProduct-models'

type Props<T extends MainProduct> = Omit<
  EventMainProductSelectionInputProps<T>,
  'arrayInputLabel'
> & {
  entity: APIEntity
}

export const TailoredEventSelectionInput = <T extends MainProduct>({
  entity,
  children,
  ...rest
}: Props<T>) => (
  <CustomReferenceInput
    entity={entity}
    filterFns={[filterByTailored(useClientAndOrganizationIdFromEvent())]}
  >
    <EventMainProductSelectionInput
      {...rest}
      label="form.fields.name"
      arrayInputLabel="orders"
    >
      {children}
    </EventMainProductSelectionInput>
  </CustomReferenceInput>
)

/**
 * The filter fn that returns true when
 * - there is not a tailored customer and organization and the product is visible in SF
 * - there is a tailored and the selected customer is the correct one
 * @param organizationId the organization id if present
 * @param customerId the customer id if there is one
 * @param product the product to filter
 * @returns true if one of the above conditions is met
 */
export const filterByTailored =
  <T extends TailoredProduct & WithIdAndName>({
    organizationId,
    customerId,
  }: {
    organizationId?: number
    customerId?: number
  }) =>
  (product: T): boolean => {
    if (
      !product.customers.length &&
      !product.organizations.length &&
      isProductVisibleInStore(product)
    )
      return true

    return (
      (!!customerId && product.customers.includes(customerId)) ||
      (!!organizationId && product.organizations.includes(organizationId))
    )
  }

/**
 * Function to invoke in event form that returns cliecustomernt id if pmi and organization id if presetn
 * @returns object containing organization id and customer id
 */
export const useClientAndOrganizationIdFromEvent = () => {
  const { getValues } = useFormContext<MappedEnrichedEvent>()
  // Retrieves from the form the selected customer
  const selectedCustomer = getValues('customer')

  // Fetches the customers to get the selected one
  const customers = [
    ...useKGetList<Customer>('pmi'),
    ...useKGetList<Supplier>('suppliers'),
  ]
  if (selectedCustomer) {
    const customer = getById(customers, selectedCustomer)

    // retrieve the organization id from the customer membership
    const organizationId = customer?.membership?.organizationId
    return { customerId: selectedCustomer, organizationId }
  }
  return {}
}
